// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import React from 'react';
import { Typography } from '@kintent/glide';
import { Link } from 'react-navi';
import { useTheme } from '@emotion/react';

import * as Styles from './NavigationBarV2.styles';
import { TimestampLabel } from '../PageElements';
import { Flex } from '../Flex';
import Heartbeat from '../Heartbeat';
import CustomButton from '../CustomButton/CustomButton';
import { NoLogoLabel, StyledTrustShareLogo } from '../NavigationBar';
import { useAuthService, useCustomerLogo, useTeamLogo } from '../../lib/state';
import { SERVICE_NOW_LOGIN_URL } from '../../lib/constants';

// Assets
import { ReactComponent as TrustShareLogo } from '../../assets/ts-logo.svg';

// Constants
const NAVIGATION_ITEMS = [
  {
    title: 'Home',
    path: '/home',
    exact: true,
  },
  {
    title: 'Notifications',
    path: '/notifications',
    exact: false,
  },
  {
    title: 'Documents',
    path: '/documents',
    exact: false,
  },
  {
    title: 'Certifications',
    path: '/certifications',
    exact: false,
  },
];

function Logo() {
  const { currentTeam } = useAuthService();
  const [teamLogo] = useTeamLogo(currentTeam?.id);
  const [trustShareLogo] = useCustomerLogo();

  const logoToDisplay = trustShareLogo ?? teamLogo ?? null;

  return (
    <div>
      {logoToDisplay ? (
        <Styles.TrustShareLogoContainer
          href={currentTeam?.websiteUrl}
          target="_blank"
        >
          <StyledTrustShareLogo
            src={logoToDisplay}
            alt={`${currentTeam?.name} TrustShare logo`}
          />
        </Styles.TrustShareLogoContainer>
      ) : (
        <NoLogoLabel>
          <Typography level="8">{currentTeam?.name}</Typography>
        </NoLogoLabel>
      )}
    </div>
  );
}

function TrustCloudReferenceBlock() {
  const theme = useTheme();
  return (
    <Styles.TrustCloudCTA>
      <Flex
        alignItems="center"
        gap="8px"
      >
        <TrustShareLogo color={theme.components.header.trustShareLabel.textColor} />
        <Heartbeat strokeColor={theme.components.header.connectionIndicator.backgroundColor} />
      </Flex>
      <TimestampLabel />
    </Styles.TrustCloudCTA>
  );
}

function NavigationBarV2() {
  return (
    <Styles.Navigation data-testid="enterprise-navigation-bar">
      <Styles.LogoContainer data-testid="navigation-bar-logo-container">
        <Logo />
      </Styles.LogoContainer>
      <Styles.MenuItemsContainer data-testid="navigation-bar-items-container">
        <Styles.NavigationItemList data-testid="navigation-bar-items-list">
          {NAVIGATION_ITEMS.map((item) => {
            return (
              <li key={item.title}>
                <Styles.NavigationItem
                  as={Link}
                  color="white"
                  activeClassName="active"
                  href={item.path}
                  data-testid={`navigation-bar-item-${item.title.toLowerCase()}`}
                >
                  {item.title}
                </Styles.NavigationItem>
              </li>
            );
          })}
        </Styles.NavigationItemList>
      </Styles.MenuItemsContainer>
      <Flex
        gap="12px"
        className="login-actions-container"
      >
        <TrustCloudReferenceBlock />
        <CustomButton
          data-testid="enterprise-grade-login-url"
          asChild
        >
          <Link
            href={SERVICE_NOW_LOGIN_URL}
            target="_blank"
          >
            Sign Up
          </Link>
        </CustomButton>
      </Flex>
    </Styles.Navigation>
  );
}

export default NavigationBarV2;
